import { useState, useRef, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress, Chip,
  Link,
} from '@mui/material';
import ExploreIcon from '@mui/icons-material/Explore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LoginIcon from '@mui/icons-material/Login';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

/**
 * Two-step email OTP login.
 *
 * Why OTP and not magic link? On iOS, when the app is installed as a PWA
 * (Add to Home Screen), tapping a magic link in the user's email opens the
 * link in Safari — which is a separate sandbox from the standalone PWA. The
 * Safari session is created, but the PWA never sees it, so the user
 * appears stuck on the login screen even though "auth worked". Magic links
 * can't bridge that gap because Apple doesn't support deep linking for PWAs.
 *
 * 6-digit OTP codes solve this cleanly: the email contains a number, the
 * user types it into whichever surface they're on (browser tab OR PWA),
 * and the session is created on the surface that did the verify call.
 */
export default function Login() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [step, setStep] = useState('email');     // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const codeInputRef = useRef(null);

  // Autofocus the code field when we move to step 2
  useEffect(() => {
    if (step === 'code' && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  // Already logged in? Bounce to home.
  if (!authLoading && user) return <Navigate to="/" replace />;

  // ─── Step 1: send the OTP code ────────────────────────────────────────
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setError(null);

    // signInWithOtp sends both a magic link AND a 6-digit code in the same
    // email (depending on Supabase email template). We expect the user to
    // type the code; the link still works as a fallback for browser users.
    const { error: signInError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        shouldCreateUser: true,
        // Magic link still set for browser fallback users; PWA users use code.
        emailRedirectTo: window.location.origin,
      },
    });

    setSending(false);
    if (signInError) {
      // Supabase emits raw lowercase strings like "email rate limit exceeded"
      // and "user not allowed". Map the known ones to friendlier i18n
      // versions so the UI doesn't expose backend prose. Unknown errors fall
      // back to the generic send_error.
      const raw = (signInError.message || '').toLowerCase();
      let friendly;
      if (raw.includes('rate limit')) {
        friendly = t('login.error_rate_limit');
      } else if (raw.includes('not allowed') || raw.includes('not on the')) {
        friendly = t('login.error_not_allowed');
      } else if (raw.includes('invalid email')) {
        friendly = t('login.error_invalid_email');
      } else {
        friendly = t('login.send_error');
      }
      setError(friendly);
      return;
    }
    setStep('code');
  };

  // ─── Step 2: verify the OTP code ──────────────────────────────────────
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const cleaned = code.replace(/\D/g, '').slice(0, 6);
    if (cleaned.length !== 6) {
      setError(t('login.code_invalid'));
      return;
    }
    setVerifying(true);
    setError(null);

    const { error: verifyError } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: cleaned,
      type: 'email',
    });

    setVerifying(false);
    if (verifyError) {
      setError(verifyError.message || t('login.verify_error'));
      return;
    }
    // Successful sign-in: the AuthContext picks up the session via Supabase's
    // onAuthStateChange listener, the user prop becomes truthy, and the
    // Navigate at the top of this component bounces to /.
  };

  const handleResend = async () => {
    setCode('');
    setStep('email');
    setError(null);
  };

  return (
    <Box
      display="flex" alignItems="center" justifyContent="center"
      sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: 'background.default', px: 2 }}
    >
      <Paper
        variant="outlined"
        sx={{ p: { xs: 3, sm: 4 }, maxWidth: 420, width: '100%', borderRadius: 3 }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <ExploreIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700} color="primary.main">
            Career Compass
          </Typography>
          <Chip label={t('common.beta')} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
        </Box>

        {/* ─── STEP 1: ask for email ───────────────────────────────── */}
        {step === 'email' && (
          <>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t('login.subtitle_email')}
            </Typography>
            <form onSubmit={handleSendCode}>
              <TextField
                label={t('login.email_label')}
                type="email"
                fullWidth
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={sending}
                autoComplete="email"
                sx={{ mb: 2 }}
              />
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={sending || !email.trim()}
                startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <MailOutlineIcon />}
              >
                {sending ? t('login.sending') : t('login.send_code')}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                {t('login.closed_beta_note')}
              </Typography>
            </form>
          </>
        )}

        {/* ─── STEP 2: ask for the code ────────────────────────────── */}
        {step === 'code' && (
          <>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {/* Use <Trans> so the <strong> tag is rendered, not escaped.
                  Plain t() returns the raw string with HTML literal — see
                  i18n config which whitelists <strong> for <Trans> only. */}
              <Trans
                i18nKey="login.subtitle_code"
                values={{ email }}
                components={{ strong: <strong /> }}
              />
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" mb={3}>
              {t('login.code_hint')}
            </Typography>

            <form onSubmit={handleVerifyCode}>
              <TextField
                label={t('login.code_label')}
                inputRef={codeInputRef}
                fullWidth
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={verifying}
                autoComplete="one-time-code"
                inputMode="numeric"
                inputProps={{
                  maxLength: 6,
                  pattern: '[0-9]*',
                  style: { fontSize: 24, letterSpacing: 8, textAlign: 'center' },
                }}
                placeholder="000000"
                sx={{ mb: 2 }}
              />
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={verifying || code.length !== 6}
                startIcon={verifying ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
              >
                {verifying ? t('login.verifying') : t('login.sign_in')}
              </Button>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={handleResend}
                  underline="hover"
                  sx={{ color: 'text.secondary' }}
                >
                  ← {t('login.back_email')}
                </Link>
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={handleSendCode}
                  underline="hover"
                  disabled={sending}
                  sx={{ color: 'primary.main' }}
                >
                  {sending ? t('login.sending') : t('login.resend')}
                </Link>
              </Box>
            </form>
          </>
        )}
      </Paper>
    </Box>
  );
}
